import styled from 'styled-components';
import {
    ANIMATION_CURVE,
    ANIMATION_DURATION,
    getSpacingToken,
    getTextTokenPreset,
    getToken,
} from '@optimus-web/common';
import { LinkStyled } from '../../Link/style';
import { CSSWidth } from '../types';

const TRANSITION = `
    transition-property: background-color, border-radius, border-color, opacity;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.default}ms;
`;

export const TableElement = styled.table`
    border-spacing: ${getSpacingToken('0')};
    width: 100%;
    height: 1px;
`;

export const TableHeader = styled.thead<{ fixedHeader: boolean }>`
    position: ${(props) => (props.fixedHeader ? 'sticky' : 'relative')};
    background: ${({ fixedHeader }) =>
        fixedHeader
            ? getToken('backgroundInteractiveNeutralSubtleActive')
            : getToken('backgroundInteractiveNeutralSubtleDefault')};
    top: ${(props) => (props.fixedHeader ? '0' : 'initial')};
    z-index: ${(props) => (props.fixedHeader ? '100' : 'initial')};
    ${({ fixedHeader, theme }) => fixedHeader && `box-shadow: ${getToken('shadow100')({ theme })}`};
`;

export const TableHeaderRow = styled.tr``;

export const TableHeaderCell = styled.th<{
    fixedFirstColumn: boolean;
    fixedLastColumn: boolean;
    fixedHeader: boolean;
    interactive: boolean;
    rowSelectionEnabled: boolean;
    width?: number | CSSWidth;
    filterable?: boolean;
}>`
    ${getTextTokenPreset('bodyMediumStrong')};
    background: ${getToken('backgroundInteractiveNeutralSubtleDefault')};
    padding: ${getSpacingToken('0')}px ${getSpacingToken('200')}px;
    padding-right: ${({ filterable }) => (filterable ? getSpacingToken('100') : getSpacingToken('200'))}px;
    min-height: ${getSpacingToken('500')}px;
    border-bottom: ${getToken('borderWidth250')}px solid ${getToken('borderStaticSecondary')};
    color: ${getToken('textStaticPrimary')};
    width: ${({ width, theme }) =>
        typeof width === 'number' ? `${width - 2 * getSpacingToken('200')({ theme })}px` : width ?? 'auto'};
    ${TRANSITION};

    &:first-child {
        border-top-left-radius: ${getToken('borderRadius100')}px;
        position: ${({ fixedFirstColumn }) => (fixedFirstColumn ? 'sticky' : 'relative')};
        z-index: ${({ fixedFirstColumn }) => (fixedFirstColumn ? '10' : 'initial')};
        left: ${({ fixedFirstColumn }) => (fixedFirstColumn ? '0' : 'initial')};
        width: ${({ rowSelectionEnabled, width, theme }) => {
            if (rowSelectionEnabled) {
                return `${getSpacingToken('300')({ theme })}px`;
            }
            return typeof width === 'number' ? `${width - 2 * getSpacingToken('200')({ theme })}px` : width ?? 'auto';
        }};
    }

    &:last-child {
        border-top-right-radius: ${getToken('borderRadius100')}px;
        position: ${({ fixedLastColumn }) => (fixedLastColumn ? 'sticky' : 'relative')};
        right: ${({ fixedLastColumn }) => (fixedLastColumn ? '0' : 'initial')};
        z-index: ${({ fixedLastColumn }) => (fixedLastColumn ? '10' : 'initial')};
    }

    &:nth-of-type(2) {
        position: ${({ fixedFirstColumn, rowSelectionEnabled }) =>
            fixedFirstColumn && rowSelectionEnabled ? 'sticky' : 'relative'};
    }
`;

export const TableBody = styled.tbody<{ isLoading?: boolean }>`
    pointer-events: ${({ isLoading }) => (isLoading ? 'none' : 'initial')};
`;

export const TableCellInner = styled.div<{ align?: 'start' | 'end' }>`
    min-height: calc(${getSpacingToken('700')}px - 1px);
    height: 100%;
    padding: ${getSpacingToken('100')}px ${getSpacingToken('200')}px;
    display: flex;
    align-items: center;
    justify-content: ${({ align }) => (align ? `flex-${align}` : 'flex-start')};
    ${TRANSITION};
    box-sizing: border-box;

    ${LinkStyled} {
        text-decoration: none;
    }
`;

export const TableCell = styled.td<{
    fixedFirstColumn: boolean;
    fixedLastColumn: boolean;
    rowSelectionEnabled: boolean;
}>`
    height: 100%;
    ${getTextTokenPreset('bodyMedium')};
    color: ${getToken('textStaticPrimary')};
    padding: ${getSpacingToken('0')};
    ${TRANSITION};

    &:first-child {
        background-color: ${getToken('backgroundStaticFloating')};
        position: ${({ fixedFirstColumn }) => (fixedFirstColumn ? 'sticky' : 'relative')};
        z-index: ${({ fixedFirstColumn }) => (fixedFirstColumn ? '10' : 'initial')};
        left: ${({ fixedFirstColumn }) => (fixedFirstColumn ? '0' : 'initial')};
    }

    &:nth-of-type(2) {
        position: ${({ fixedFirstColumn, rowSelectionEnabled }) =>
            fixedFirstColumn && rowSelectionEnabled ? 'sticky' : 'relative'};
    }

    &:last-child {
        position: ${({ fixedLastColumn }) => (fixedLastColumn ? 'sticky' : 'relative')};
        right: ${({ fixedLastColumn }) => (fixedLastColumn ? '0' : 'initial')};
        z-index: ${({ fixedLastColumn }) => (fixedLastColumn ? '10' : 'initial')};
    }
`;

export const TableBodyRow = styled.tr<{ active?: boolean; nextIsActive?: boolean; previousIsActive?: boolean }>`
    position: relative;
    height: 100%;
    /* fix browsers like Safari ignoring relative position for table, tr, td, etc. elements. See https://www.w3.org/TR/CSS2/visuren.html#choose-position */
    transform: scale(1, 1);

    ${TableCell} {
        border-bottom: 1px solid
            ${({ active }) => (active ? getToken('borderStaticInverse') : getToken('borderStaticSecondary'))};

        &:first-child {
            ${TableCellInner} {
                border-top-left-radius: ${({ active, previousIsActive, theme }) =>
                    active && previousIsActive
                        ? `${getToken('borderRadius0')({ theme })}px`
                        : `${getToken('borderRadius100')({ theme })}px`};
                border-bottom-left-radius: ${({ active, nextIsActive, theme }) =>
                    active && nextIsActive
                        ? `${getToken('borderRadius0')({ theme })}px`
                        : `${getToken('borderRadius100')({ theme })}px`};
            }
        }

        &:last-child {
            ${TableCellInner} {
                border-top-right-radius: ${({ active, previousIsActive, theme }) =>
                    active && previousIsActive
                        ? `${getToken('borderRadius0')({ theme })}px`
                        : `${getToken('borderRadius100')({ theme })}px`};
                border-bottom-right-radius: ${({ active, nextIsActive, theme }) =>
                    active && nextIsActive
                        ? `${getToken('borderRadius0')({ theme })}px`
                        : `${getToken('borderRadius100')({ theme })}px`};
            }
        }
    }

    ${TableCellInner} {
        background-color: ${({ active }) =>
            active
                ? getToken('backgroundInteractiveSecondaryDefault')
                : getToken('backgroundInteractiveNeutralSubtleDefault')};
    }

    &::after {
        content: '';
        width: 100%;
        height: ${getSpacingToken('25')}px;
        background-color: ${getToken('backgroundInteractiveNeutralSubtleDefault')};
        position: absolute;
        top: -1px;
        left: 0;
        opacity: ${({ active }) => (active ? getToken('opacity1000') : getToken('opacity0'))};
        ${TRANSITION};
        z-index: 12;
    }

    &:first-child {
        &::after {
            height: 0;
        }

        ${TableCell} {
            &:first-child {
                ${TableCellInner} {
                    border-top-left-radius: ${getToken('borderRadius0')}px;
                }
            }

            &:last-child {
                ${TableCellInner} {
                    border-top-right-radius: ${getToken('borderRadius0')}px;
                }
            }
        }
    }

    &:hover {
        &::after {
            opacity: ${getToken('opacity1000')};
            height: ${getSpacingToken('25')}px;
        }

        ${TableCell} {
            border-bottom: 1px solid ${getToken('borderStaticInverse')};
        }

        &:first-child {
            &::after {
                opacity: ${getToken('opacity1000')};
                height: ${getSpacingToken('0')};
            }

            ${TableCell} {
                &:first-child {
                    ${TableCellInner} {
                        border-top-left-radius: ${getToken('borderRadius0')}px;
                    }
                }

                &:last-child {
                    ${TableCellInner} {
                        border-top-right-radius: ${getToken('borderRadius0')}px;
                    }
                }
            }
        }

        ${TableCellInner} {
            background-color: ${({ active }) =>
                active
                    ? getToken('backgroundInteractiveSecondaryHover')
                    : getToken('backgroundInteractiveNeutralSubtleHover')};

            ${LinkStyled} {
                text-decoration: underline;

                &:hover {
                    text-decoration: none;
                }
            }
        }
    }
`;

export const TableContainer = styled.div`
    overflow: auto;
    max-width: 100%;
    max-height: 100%;
`;

export const TableWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 100%;
`;
